import type { RoiLine, RoiPeriod } from './types';

/** Rounds to whole cents. */
export function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

const PERIODS_PER_YEAR: Record<RoiPeriod, number> = {
  OneTime: 0,
  PerMonth: 12,
  PerQuarter: 4,
  PerYear: 1,
};

/** Amount expressed per year; a one-time amount contributes nothing to the annual figure. */
export function annualise(amount: number, period: RoiPeriod): number {
  return roundCents(amount * PERIODS_PER_YEAR[period]);
}

/** Yearly labor saving: hours per week x hourly rate x 52 weeks. */
export function laborAnnual(hoursPerWeek: number, hourlyRate: number): number {
  return roundCents(hoursPerWeek * hourlyRate * 52);
}

/** True when a Labor line has both helper inputs, so its amount is computed (per year). */
export function isComputedLabor(line: RoiLine): boolean {
  return line.kind === 'Labor' && line.hoursPerWeek !== null && line.hourlyRate !== null;
}

/** The line's effective amount: computed for a Labor line with both helper inputs, else as entered. */
export function lineAmount(line: RoiLine): number {
  if (isComputedLabor(line)) return laborAnnual(line.hoursPerWeek as number, line.hourlyRate as number);
  return roundCents(line.amount);
}

/** Sums lines into a one-time total and a per-year total. */
export function breakdownTotals(lines: readonly RoiLine[]): { oneTime: number; annual: number } {
  let oneTime = 0;
  let annual = 0;
  for (const line of lines) {
    const amount = lineAmount(line);
    const period: RoiPeriod = isComputedLabor(line) ? 'PerYear' : line.period;
    if (period === 'OneTime') oneTime += amount;
    else annual += annualise(amount, period);
  }
  return { oneTime: roundCents(oneTime), annual: roundCents(annual) };
}

/** Cost to build: tokens plus additional cost, nulls counted as 0. */
export function buildCost(costs: { tokensCost: number | null; additionalCost: number | null }): number {
  return roundCents((costs.tokensCost ?? 0) + (costs.additionalCost ?? 0));
}

/** First-year net: one-time saving plus one year of recurring saving, minus the cost to build. */
export function netFirstYear(oneTime: number, annual: number, cost: number): number {
  return roundCents(oneTime + annual - cost);
}

/** Months of recurring saving needed to cover the cost, to 1 decimal; null with no cost or no saving. */
export function paybackMonths(cost: number, annual: number): number | null {
  if (annual <= 0 || cost <= 0) return null;
  return Math.round((cost / (annual / 12)) * 10) / 10;
}
